<template>
  <div class="modal-overlay" @click="close">
    <div class="editor-modal" @click.stop>
      <!-- Header -->
      <div class="modal-header">
        <h2>{{ isEditing ? 'Edit Recipe' : 'Add Recipe' }}</h2>
        <button class="close-btn" @click="close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <!-- Form -->
      <div class="modal-body">
        <form @submit.prevent="save">
          <!-- Basic Info -->
          <div class="form-section">
            <h3>Basic Information</h3>

            <div class="form-group">
              <label>Recipe Name *</label>
              <input
                v-model="form.name"
                type="text"
                placeholder="Enter recipe name"
                required
              />
            </div>

            <div class="form-group">
              <label>Description</label>
              <textarea
                v-model="form.description"
                placeholder="Brief description of the recipe"
                rows="2"
              ></textarea>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Meal Type *</label>
                <select v-model="form.meal_type" required>
                  <option value="">Select type</option>
                  <option v-for="type in mealTypes" :key="type.value" :value="type.value">
                    {{ type.label }}
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label>Day Context</label>
                <select v-model="form.context">
                  <option value="">Any day</option>
                  <option value="match_day">Match Day</option>
                  <option value="carb_loading">Carb Loading (MD-1)</option>
                  <option value="preparation">Preparation (MD-2)</option>
                  <option value="recovery_day">Recovery Day (MD+1)</option>
                  <option value="training_day">Training Day</option>
                  <option value="rest_day">Rest Day</option>
                </select>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Category</label>
                <select v-model="form.category">
                  <option value="">Select category</option>
                  <option v-for="cat in categories" :key="cat.value" :value="cat.value">
                    {{ cat.label }}
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label>Difficulty</label>
                <select v-model="form.difficulty">
                  <option value="">Select difficulty</option>
                  <option v-for="diff in difficulties" :key="diff.value" :value="diff.value">
                    {{ diff.label }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label>Prep Time (minutes)</label>
              <input
                v-model.number="form.prep_time"
                type="number"
                min="0"
                placeholder="e.g., 15"
              />
            </div>
          </div>

          <!-- Macros -->
          <div class="form-section">
            <h3>Nutrition Information</h3>

            <div class="macros-grid">
              <div class="macro-input">
                <label>Calories</label>
                <div class="input-with-unit">
                  <input
                    v-model.number="form.calories"
                    type="number"
                    min="0"
                    placeholder="0"
                  />
                  <span class="unit">kcal</span>
                </div>
              </div>

              <div class="macro-input">
                <label>Carbs</label>
                <div class="input-with-unit">
                  <input
                    v-model.number="form.carbs"
                    type="number"
                    min="0"
                    placeholder="0"
                  />
                  <span class="unit">g</span>
                </div>
              </div>

              <div class="macro-input">
                <label>Protein</label>
                <div class="input-with-unit">
                  <input
                    v-model.number="form.protein"
                    type="number"
                    min="0"
                    placeholder="0"
                  />
                  <span class="unit">g</span>
                </div>
              </div>

              <div class="macro-input">
                <label>Fat</label>
                <div class="input-with-unit">
                  <input
                    v-model.number="form.fat"
                    type="number"
                    min="0"
                    placeholder="0"
                  />
                  <span class="unit">g</span>
                </div>
              </div>
            </div>

            <div class="auto-calc-note">
              <span v-if="form.carbs || form.protein || form.fat">
                Estimated calories: {{ calculatedCalories }} kcal
              </span>
            </div>
          </div>

          <!-- Dietary Tags -->
          <div class="form-section">
            <h3>Dietary Information</h3>

            <div class="form-group">
              <label>Dietary Tags</label>
              <div class="tags-grid">
                <label
                  v-for="tag in dietaryTags"
                  :key="tag.value"
                  class="tag-checkbox"
                >
                  <input
                    type="checkbox"
                    :value="tag.value"
                    v-model="selectedTags"
                  />
                  <span class="tag-label" :style="{ borderColor: tag.color }">
                    {{ tag.label }}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <!-- Ingredients -->
          <div class="form-section">
            <h3>Ingredients</h3>

            <div class="form-group">
              <label>Ingredients List</label>
              <textarea
                v-model="form.ingredients"
                placeholder="List ingredients, one per line or comma-separated"
                rows="4"
              ></textarea>
            </div>
          </div>

          <!-- Timing (for match day meals) -->
          <div v-if="isMatchDayMeal" class="form-section">
            <h3>Match Day Timing</h3>

            <div class="form-row">
              <div class="form-group">
                <label>Hours Before Match</label>
                <input
                  v-model.number="form.timing_hours_before"
                  type="number"
                  min="0"
                  max="24"
                  placeholder="e.g., 3"
                />
              </div>

              <div class="form-group">
                <label>Hours After Match</label>
                <input
                  v-model.number="form.timing_hours_after"
                  type="number"
                  min="0"
                  max="24"
                  placeholder="e.g., 1"
                />
              </div>
            </div>
          </div>
        </form>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button type="button" class="cancel-btn" @click="close">
          Cancel
        </button>
        <button
          type="button"
          class="save-btn"
          :disabled="saving || !isValid"
          @click="save"
        >
          {{ saving ? 'Saving...' : (isEditing ? 'Update Recipe' : 'Add Recipe') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Nutrition } from "@/services/nutrition-service";

export default {
  name: "RecipeEditorModal",

  props: {
    recipe: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      saving: false,
      form: {
        name: "",
        description: "",
        meal_type: "",
        context: "",
        category: "",
        difficulty: "",
        prep_time: null,
        calories: null,
        carbs: null,
        protein: null,
        fat: null,
        ingredients: "",
        timing_hours_before: null,
        timing_hours_after: null,
      },
      selectedTags: [],
      mealTypes: Nutrition.getAllMealTypes(),
      categories: Nutrition.getCategories(),
      difficulties: Nutrition.getDifficultyLevels(),
      dietaryTags: Nutrition.getDietaryTags(),
    };
  },

  computed: {
    isEditing() {
      return !!this.recipe?.id;
    },

    isValid() {
      return this.form.name && this.form.meal_type;
    },

    calculatedCalories() {
      return Nutrition.calculateCalories(
        this.form.carbs || 0,
        this.form.protein || 0,
        this.form.fat || 0
      );
    },

    isMatchDayMeal() {
      return (
        this.form.meal_type === "pre_match" ||
        this.form.meal_type === "post_match" ||
        this.form.context === "match_day"
      );
    },
  },

  mounted() {
    if (this.recipe) {
      this.populateForm();
    }
    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden";
  },

  beforeUnmount() {
    document.body.style.overflow = "";
  },

  methods: {
    populateForm() {
      this.form = {
        name: this.recipe.name || "",
        description: this.recipe.description || "",
        meal_type: this.recipe.meal_type || "",
        context: this.recipe.context || "",
        category: this.recipe.category || "",
        difficulty: this.recipe.difficulty || "",
        prep_time: this.recipe.prep_time || null,
        calories: this.recipe.calories || null,
        carbs: this.recipe.carbs || null,
        protein: this.recipe.protein || null,
        fat: this.recipe.fat || null,
        ingredients: this.recipe.ingredients || "",
        timing_hours_before: this.recipe.timing_hours_before || null,
        timing_hours_after: this.recipe.timing_hours_after || null,
      };

      // Parse dietary tags - can be array (from API) or comma-separated string
      if (this.recipe.dietary_tags) {
        if (Array.isArray(this.recipe.dietary_tags)) {
          this.selectedTags = this.recipe.dietary_tags;
        } else {
          this.selectedTags = this.recipe.dietary_tags.split(",").map((t) => t.trim());
        }
      }
    },

    close() {
      this.$emit("close");
    },

    async save() {
      if (!this.isValid || this.saving) return;

      this.saving = true;
      try {
        const data = {
          ...this.form,
          dietary_tags: this.selectedTags.join(","),
        };

        if (this.isEditing) {
          data.id = this.recipe.id;
        }

        this.$emit("save", data);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.editor-modal {
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;

  h2 {
    margin: 0;
    font-size: 1.25rem;
    color: #333;
  }
}

.close-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: #f0f0f0;
    color: #333;
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.form-section {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  h3 {
    margin: 0 0 12px;
    font-size: 1rem;
    color: #333;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
  }
}

.form-group {
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 0.9rem;
    font-weight: 500;
    color: #333;
    margin-bottom: 6px;
  }

  input,
  textarea,
  select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 0.95rem;

    &:focus {
      outline: none;
      border-color: #007bff;
    }
  }

  textarea {
    resize: vertical;
  }
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.macros-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.macro-input {
  label {
    display: block;
    font-size: 0.85rem;
    color: #666;
    margin-bottom: 4px;
  }
}

.input-with-unit {
  display: flex;
  align-items: center;

  input {
    flex: 1;
    border-radius: 8px 0 0 8px;
    border-right: none;
  }

  .unit {
    padding: 10px 12px;
    background: #f8f9fa;
    border: 1px solid #ddd;
    border-radius: 0 8px 8px 0;
    color: #666;
    font-size: 0.85rem;
  }
}

.auto-calc-note {
  margin-top: 8px;
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
}

.tags-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tag-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;

  input {
    display: none;
  }

  .tag-label {
    padding: 6px 14px;
    border-radius: 16px;
    border: 2px solid #ddd;
    font-size: 0.85rem;
    transition: all 0.2s;
  }

  input:checked + .tag-label {
    background: #007bff;
    border-color: #007bff;
    color: white;
  }
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 20px;
  border-top: 1px solid #eee;
  background: #f8f9fa;
}

.cancel-btn {
  padding: 10px 20px;
  background: white;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #f0f0f0;
  }
}

.save-btn {
  padding: 10px 24px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #1e7e34;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
}

/* Mobile adjustments */
@media screen and (max-width: 480px) {
  .modal-overlay {
    padding: 0;
  }

  .editor-modal {
    max-height: 100vh;
    border-radius: 0;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .macros-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
